import React from 'react';
import { Link } from 'react-router-dom';

export const TermsAndConditions = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="text-xl font-bold text-primary-600">Drive On Wheels</div>
          <Link to="/login" className="text-sm text-primary-600 hover:text-primary-700 font-medium">
            Login →
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <article className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sm:p-10 prose-styled">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms and Conditions</h1>
          <p className="text-sm text-gray-500 mb-8">Last updated: August 20, 2026</p>

          <p className="text-gray-700 leading-relaxed mb-6">
            Please read these Terms and Conditions carefully before using the Drive On Wheels Lead
            Management System (the "Service") operated by Drive On Wheels ("We", "Us", "Our"). Your access
            to and use of the Service is conditioned on Your acceptance of and compliance with these Terms.
            By accessing or using the Service, You agree to be bound by these Terms. If You disagree with
            any part of these Terms, You may not access the Service.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Interpretation and Definitions</h2>

          <h3 className="text-xl font-semibold text-gray-800 mt-6 mb-3">Definitions</h3>
          <p className="text-gray-700 leading-relaxed mb-4">For the purposes of these Terms and Conditions:</p>
          <ul className="list-disc pl-6 space-y-2 text-gray-700">
            <li><strong>Account</strong> means a unique account created for You to access our Service or parts of our Service.</li>
            <li><strong>Company</strong> (referred to as either "the Company", "We", "Us" or "Our" in this Agreement) refers to Drive On Wheels, GHAZIABAD, UP.</li>
            <li><strong>Service</strong> refers to the Drive On Wheels Lead Management System, accessible from <a href="https://lms.driveonwheels.com" className="text-primary-600 hover:underline">https://lms.driveonwheels.com</a>.</li>
            <li><strong>Workspace</strong> means the isolated tenant environment assigned to a Super Admin and their downline team within the Service.</li>
            <li><strong>Content</strong> means any lead data, customer information, or other material submitted, uploaded, or generated by You through the Service.</li>
            <li><strong>You</strong> means the individual accessing or using the Service, or the company, dealership, or other legal entity on behalf of which such individual is accessing or using the Service.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Accounts</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            When You create an account with Us, You must provide accurate, complete, and current information
            at all times. Failure to do so constitutes a breach of these Terms, which may result in
            immediate termination of Your account.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3">
            You are responsible for safeguarding any password used to access the Service and for any
            activities or actions taken under Your account. You agree not to disclose Your password to any
            third party. You must notify Us immediately upon becoming aware of any breach of security or
            unauthorized use of Your account.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3">
            Access levels (Company, Super Admin, Admin, Dealer, Sales Executive) are assigned based on Your
            role within Your organization's workspace, and Your permitted actions within the Service are
            scoped accordingly.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Use of the Service</h2>
          <p className="text-gray-700 leading-relaxed mb-3">You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to:</p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>Use the Service in any way that violates any applicable local, state, national, or international law or regulation.</li>
            <li>Attempt to gain unauthorized access to any other user's workspace, account, or data.</li>
            <li>Upload or submit any Content that is unlawful, defamatory, or infringes on the rights of any third party.</li>
            <li>Use the Service to send unsolicited communications in violation of applicable telemarketing or messaging laws.</li>
            <li>Interfere with or disrupt the integrity or performance of the Service or its underlying infrastructure.</li>
            <li>Attempt to reverse engineer, decompile, or extract the source code of the Service.</li>
          </ul>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Third-Party Integrations</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            The Service may integrate with third-party platforms, including Meta/Facebook Lead Ads, Google,
            and WhatsApp, to import or sync lead data on Your behalf. Your use of such integrations is also
            subject to the respective third party's own terms of service and privacy policy. We are not
            responsible for the availability, accuracy, or practices of these third-party platforms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Content and Data Ownership</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            You retain all ownership rights to the Content You submit to the Service, including customer and
            lead data. By using the Service, You grant Us a limited license to store, process, and display
            that Content solely for the purpose of providing the Service to You.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3">
            You are solely responsible for the Content You submit and for ensuring You have the necessary
            rights and consents (including under applicable data protection laws) to collect and process the
            personal data of Your customers and leads through the Service.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Subscription and Payment</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            Access to the Service may be subject to a subscription plan and user-count limits assigned to
            Your workspace. We reserve the right to suspend or restrict access to the Service for accounts
            with overdue payments, in accordance with the commercial agreement between You and Us.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Termination</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            We may terminate or suspend Your account immediately, without prior notice or liability, for any
            reason, including without limitation if You breach these Terms. Upon termination, Your right to
            use the Service will cease immediately.
          </p>
          <p className="text-gray-700 leading-relaxed mb-3">
            You may request deletion of Your account and associated data at any time. See our{' '}
            <Link to="/data-deletion" className="text-primary-600 hover:underline">Data Deletion page</Link>{' '}
            for details.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Limitation of Liability</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            To the maximum extent permitted by applicable law, in no event shall the Company be liable for
            any indirect, incidental, special, consequential, or punitive damages, including loss of profits,
            data, or other intangible losses, resulting from Your access to or use of, or inability to
            access or use, the Service.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Disclaimer</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Company makes no warranties,
            expressed or implied, regarding the operation of the Service or the information, content, or
            materials included in it, including uninterrupted or error-free operation.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Governing Law</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            These Terms shall be governed and construed in accordance with the laws of Uttar Pradesh, India,
            without regard to its conflict of law provisions.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Changes to These Terms</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            We reserve the right, at Our sole discretion, to modify or replace these Terms at any time. We
            will notify You of any changes by posting the updated Terms on this page and updating the "Last
            updated" date above. Your continued use of the Service after any such changes constitutes Your
            acceptance of the new Terms.
          </p>

          <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-4">Contact Us</h2>
          <p className="text-gray-700 leading-relaxed mb-3">
            If you have any questions about these Terms and Conditions, You can contact us:
          </p>
          <ul className="list-disc pl-6 space-y-1 text-gray-700">
            <li>By email: <a href="mailto:padeyvarun03@gmail.com" className="text-primary-600 hover:underline">padeyvarun03@gmail.com</a></li>
            <li>By visiting this page on our website: <a href="https://lms.driveonwheels.com/" className="text-primary-600 hover:underline">https://lms.driveonwheels.com/</a></li>
            <li>By phone number: +91 9818864690</li>
          </ul>
        </article>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap items-center justify-between text-sm text-gray-500">
          <p>&copy; {new Date().getFullYear()} Drive On Wheels. All rights reserved.</p>
          <div className="flex gap-4">
            <Link to="/privacy-policy" className="hover:text-primary-600">Privacy Policy</Link>
            <Link to="/terms-and-conditions" className="hover:text-primary-600">Terms and Conditions</Link>
            <Link to="/data-deletion" className="hover:text-primary-600">Data Deletion</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
